import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type VehicleImageRow = Database['public']['Tables']['vehicle_images']['Row'];

export interface VehicleImage {
  id: string;
  service_type: 'standard' | 'vip';
  min_passengers: number;
  max_passengers: number;
  image_url: string;
  vehicle_name: string;
  description?: string;
  features: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useVehicleImages = () => {
  const [vehicleImages, setVehicleImages] = useState<VehicleImage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVehicleImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vehicle_images')
        .select('*')
        .order('service_type', { ascending: true })
        .order('min_passengers', { ascending: true });

      if (error) throw error;
      setVehicleImages((data || []) as VehicleImage[]);
    } catch (error) {
      console.error('Error fetching vehicle images:', error);
      toast.error('Error al cargar las imágenes de vehículos');
    } finally {
      setLoading(false);
    }
  };

  const getVehicleByPassengers = (serviceType: 'standard' | 'vip', passengers: number): VehicleImage | null => {
    return vehicleImages.find(
      vehicle => 
        vehicle.service_type === serviceType &&
        vehicle.is_active &&
        passengers >= vehicle.min_passengers && 
        passengers <= vehicle.max_passengers
    ) || null;
  };

  const createVehicleImage = async (vehicleData: Omit<VehicleImage, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('vehicle_images')
        .insert([vehicleData])
        .select()
        .single();

      if (error) throw error;
      
      setVehicleImages(prev => [...prev, data as VehicleImage]);
      toast.success('Configuración de vehículo creada exitosamente');
      return data;
    } catch (error) {
      console.error('Error creating vehicle image:', error);
      toast.error('Error al crear la configuración de vehículo');
      throw error;
    }
  };

  const updateVehicleImage = async (id: string, updates: Partial<VehicleImage>) => {
    try {
      const { data, error } = await supabase
        .from('vehicle_images')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setVehicleImages(prev => 
        prev.map(vehicle => vehicle.id === id ? data as VehicleImage : vehicle)
      );
      toast.success('Configuración de vehículo actualizada exitosamente');
      return data;
    } catch (error) {
      console.error('Error updating vehicle image:', error);
      toast.error('Error al actualizar la configuración de vehículo');
      throw error;
    }
  };

  const deleteVehicleImage = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vehicle_images')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setVehicleImages(prev => prev.filter(vehicle => vehicle.id !== id));
      toast.success('Configuración de vehículo eliminada exitosamente');
    } catch (error) {
      console.error('Error deleting vehicle image:', error);
      toast.error('Error al eliminar la configuración de vehículo');
      throw error;
    }
  };

  useEffect(() => {
    fetchVehicleImages();
  }, []);

  return {
    vehicleImages,
    loading,
    fetchVehicleImages,
    getVehicleByPassengers,
    createVehicleImage,
    updateVehicleImage,
    deleteVehicleImage,
  };
};